import { z } from "zod";
import { recurringScheduleSchema } from "../scheduling/recurringSchedule.js";
const MAX_GURU_COLLECTIONS = 5;
const MAX_GURU_TAGS_PER_COLLECTION = 20;
const MAX_GURU_TAG_LENGTH = 100;
function isSalesforceIntegrationSettings(settings) {
  return "instance_url" in settings && (settings.instance_url === null || typeof settings.instance_url === "string") && "is_enabled" in settings && typeof settings.is_enabled === "boolean";
}
function isSlackIntegrationSettings(settings) {
  return "channel_id" in settings && (settings.channel_id === null || typeof settings.channel_id === "string");
}
const teamsIntegrationSettingsSchema = z.object({
  tenant_id: z.string().nullable()
});
function isTeamsIntegrationSettings(settings) {
  return teamsIntegrationSettingsSchema.safeParse(settings).success;
}
function isInternalTwilioSubAccountIntegrationSettings(provider, settings) {
  return provider === IntegrationProvider.INTERNAL_TWILIO_SUB_ACCOUNT && "account_sid" in settings;
}
function isExternalTwilioSubAccountIntegrationSettings(provider, settings) {
  return provider === IntegrationProvider.EXTERNAL_TWILIO_SUB_ACCOUNT && "account_sid" in settings;
}
const ExternalTwilioPartner = {
  FOUNTAIN: "fountain"
};
function isTwilioSubAccountIntegrationSettings(provider, settings) {
  return isInternalTwilioSubAccountIntegrationSettings(provider, settings) || isExternalTwilioSubAccountIntegrationSettings(provider, settings);
}
function isGreenhouseIntegrationSettings(settings) {
  return "advancement_stage_name" in settings && (settings.advancement_stage_name === null || typeof settings.advancement_stage_name === "string") && "integration_system_user_email" in settings && (settings.integration_system_user_email === null || typeof settings.integration_system_user_email === "string");
}
function isRingCentralRecordingS3IntakeIntegrationSettings(settings) {
  return "client_id" in settings && "enc_client_secret" in settings && "enc_jwt" in settings;
}
function isRecordingS3IntakeIntegrationSettings(settings) {
  return "bucket" in settings && "example_object_key" in settings && "agent_mapping_source" in settings;
}
const kustomerOrgNamePattern = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
const kustomerOrgNameSchema = z.string().trim().min(1).regex(
  kustomerOrgNamePattern,
  "The org name must be the bare org slug (letters, digits, and hyphens only) \u2014 not a domain or URL."
);
const kustomerIntegrationSettingsSchema = z.object({
  // The `{orgName}` in `{orgName}.api.kustomerapp.com`.
  org_name: z.string(),
  // The encryption layer owns this opaque envelope.
  enc_api_key: z.string(),
  // The canonical sync targets: one Alpharun KB per id. Populated by the
  // connect flow (every listed KB) when nothing is stored; a save may narrow
  // or extend it, but the sync never runs without at least one id.
  knowledge_base_ids: z.array(z.string()),
  sync_schedule: recurringScheduleSchema.optional(),
  autosync_enabled: z.boolean().optional()
});
const kustomerIntegrationRequestSchema = z.strictObject({
  org_name: kustomerOrgNameSchema,
  api_key: z.string().trim().min(1).optional(),
  sync_schedule: recurringScheduleSchema.optional(),
  autosync_enabled: z.boolean().optional()
});
const kustomerIntegrationToolingRequestSchema = kustomerIntegrationRequestSchema.extend({
  knowledge_base_ids: z.array(z.string()).transform((ids) => [
    ...new Set(ids.map((id) => id.trim()).filter((id) => id.length))
  ]).refine((ids) => ids.length > 0, {
    message: "At least one knowledge base id is required."
  })
});
function isKustomerIntegrationSettings(settings) {
  return kustomerIntegrationSettingsSchema.safeParse(settings).success;
}
const guruStoredCollectionSchema = z.object({
  collection_id: z.string(),
  enc_collection_token: z.string(),
  knowledge_base_name: z.string(),
  tags: z.array(z.string()).optional(),
  only_verified: z.boolean().optional()
});
const guruCollectionIdRequestSchema = z.uuid().transform((id) => id.toLowerCase());
const guruRequestCollectionSchema = z.object({
  collection_id: guruCollectionIdRequestSchema,
  collection_token: z.string().trim().min(1).optional(),
  // Customer setup derives this from Guru after credentials are verified.
  knowledge_base_name: z.string().trim().min(1).optional(),
  tags: z.array(z.string().trim().min(1).max(MAX_GURU_TAG_LENGTH)).max(MAX_GURU_TAGS_PER_COLLECTION).default([]).transform((tags) => {
    const uniqueTags = /* @__PURE__ */ new Map();
    for (const tag of tags) {
      uniqueTags.set(tag.toLowerCase(), tag);
    }
    return [...uniqueTags.values()];
  }),
  only_verified: z.boolean().default(false)
});
function guruCollectionsSchema(collectionSchema) {
  return z.array(collectionSchema).min(1).max(MAX_GURU_COLLECTIONS).refine(
    (collections) => new Set(collections.map((collection) => collection.collection_id)).size === collections.length,
    { message: "Guru collection ids must be unique" }
  );
}
const guruIntegrationSettingsShapeSchema = z.object({
  collections: z.array(guruStoredCollectionSchema),
  sync_schedule: recurringScheduleSchema.optional(),
  autosync_enabled: z.boolean().optional()
});
const guruIntegrationSettingsSchema = guruIntegrationSettingsShapeSchema.extend({
  collections: guruCollectionsSchema(guruStoredCollectionSchema)
});
const guruIntegrationRequestSchema = z.object({
  collections: guruCollectionsSchema(guruRequestCollectionSchema),
  sync_schedule: recurringScheduleSchema.optional(),
  autosync_enabled: z.boolean().optional()
});
function isGuruIntegrationSettings(settings) {
  return guruIntegrationSettingsShapeSchema.safeParse(settings).success;
}
const GENESYS_REGIONS = [
  "mypurecloud.com",
  // us-east-1, Americas (US East)
  "use2.us-gov-pure.cloud",
  // us-east-2, Americas (US East 2, gov)
  "usw2.pure.cloud",
  // us-west-2, Americas (US West)
  "cac1.pure.cloud",
  // ca-central-1, Americas (Canada)
  "mxc1.pure.cloud",
  // mx-central-1, Americas (Mexico)
  "sae1.pure.cloud",
  // sa-east-1, South America (São Paulo)
  "mypurecloud.ie",
  // eu-west-1, EMEA (Dublin)
  "euw2.pure.cloud",
  // eu-west-2, EMEA (London)
  "mypurecloud.de",
  // eu-central-1, EMEA (Frankfurt)
  "euc2.pure.cloud",
  // eu-central-2, EMEA (Zurich)
  "edee1.eusc-pure.cloud",
  // eusc-de-east-1, EU Sovereign Cloud (Germany East)
  "mec1.pure.cloud",
  // me-central-1, Middle East (UAE)
  "aps1.pure.cloud",
  // ap-south-1, Asia Pacific (Mumbai)
  "apse1.pure.cloud",
  // ap-southeast-1, Asia Pacific (Singapore)
  "mypurecloud.com.au",
  // ap-southeast-2, Asia Pacific (Sydney)
  "mypurecloud.jp",
  // ap-northeast-1, Asia Pacific (Tokyo)
  "apne2.pure.cloud",
  // ap-northeast-2, Asia Pacific (Seoul)
  "apne3.pure.cloud"
  // ap-northeast-3, Asia Pacific (Osaka)
];
const genesysRegionSchema = z.enum(GENESYS_REGIONS);
const GENESYS_DEFAULT_REGION = "mypurecloud.com";
function isGenesysIntegrationSettings(settings) {
  return "client_id" in settings && "enc_client_secret" in settings && "region" in settings && "is_enabled" in settings && "last_conversation_end_at" in settings;
}
const genesysIntegrationRequestSchema = z.strictObject({
  client_id: z.string().trim().min(1).max(200),
  client_secret: z.string().trim().min(1).max(500),
  region: genesysRegionSchema
});
const IntegrationProvider = {
  FOUNTAIN: "fountain",
  INTERNAL_TWILIO_SUB_ACCOUNT: "internal_twilio_sub_account",
  EXTERNAL_TWILIO_SUB_ACCOUNT: "external_twilio_sub_account",
  SALESFORCE: "salesforce",
  SLACK: "slack",
  TEAMS: "teams",
  GREENHOUSE: "greenhouse",
  RINGCENTRAL_RECORDING_S3_INTAKE: "ringcentral_recording_s3_intake",
  RECORDING_S3_INTAKE: "recording_s3_intake",
  KUSTOMER: "kustomer",
  GURU: "guru",
  GENESYS: "genesys"
};
const allIntegrationProviders = [
  IntegrationProvider.FOUNTAIN,
  IntegrationProvider.INTERNAL_TWILIO_SUB_ACCOUNT,
  IntegrationProvider.EXTERNAL_TWILIO_SUB_ACCOUNT,
  IntegrationProvider.SALESFORCE,
  IntegrationProvider.SLACK,
  IntegrationProvider.TEAMS,
  IntegrationProvider.GREENHOUSE,
  IntegrationProvider.RINGCENTRAL_RECORDING_S3_INTAKE,
  IntegrationProvider.RECORDING_S3_INTAKE,
  IntegrationProvider.KUSTOMER,
  IntegrationProvider.GURU,
  IntegrationProvider.GENESYS
];
const integrationProviderSchema = z.enum(allIntegrationProviders);
function isIntegrationProvider(value) {
  return allIntegrationProviders.includes(value);
}
const genesysIntegrationCredentialsRequestSchema = genesysIntegrationRequestSchema.extend({
  provider: z.literal(IntegrationProvider.GENESYS)
});
const integrationCredentialsRequestSchema = z.discriminatedUnion(
  "provider",
  [genesysIntegrationCredentialsRequestSchema]
);
const genesysIntegrationUpdateSchema = z.union([
  genesysIntegrationRequestSchema,
  z.strictObject({ is_enabled: z.boolean() })
]);
export {
  ExternalTwilioPartner,
  GENESYS_DEFAULT_REGION,
  GENESYS_REGIONS,
  IntegrationProvider,
  MAX_GURU_COLLECTIONS,
  MAX_GURU_TAGS_PER_COLLECTION,
  MAX_GURU_TAG_LENGTH,
  genesysIntegrationCredentialsRequestSchema,
  genesysIntegrationRequestSchema,
  genesysIntegrationUpdateSchema,
  genesysRegionSchema,
  guruCollectionIdRequestSchema,
  guruIntegrationRequestSchema,
  guruIntegrationSettingsSchema,
  integrationCredentialsRequestSchema,
  isExternalTwilioSubAccountIntegrationSettings,
  isGenesysIntegrationSettings,
  isGreenhouseIntegrationSettings,
  isGuruIntegrationSettings,
  isIntegrationProvider,
  isInternalTwilioSubAccountIntegrationSettings,
  isKustomerIntegrationSettings,
  isRecordingS3IntakeIntegrationSettings,
  isRingCentralRecordingS3IntakeIntegrationSettings,
  isSalesforceIntegrationSettings,
  isSlackIntegrationSettings,
  isTeamsIntegrationSettings,
  isTwilioSubAccountIntegrationSettings,
  kustomerIntegrationRequestSchema,
  kustomerIntegrationSettingsSchema,
  kustomerIntegrationToolingRequestSchema,
  kustomerOrgNameSchema
};
//# sourceMappingURL=integration.js.map
